import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PHONE_COUNTRY_OPTIONS,
  clampLocalNumber,
  getPhoneLengthMessage,
  getPhoneNumberRule,
  isValidNotificationLocalNumber,
} from '@/utils/phoneUtils';

const LENGTH_HINT_ID = 'phone-length-hint';

interface NotificationPhoneFieldProps {
  countryCode: string;
  localNumber: string;
  /** Receives the new code plus the current number re-clamped to that code. */
  onCountryCodeChange: (code: string, adjustedLocalNumber: string) => void;
  onLocalNumberChange: (localNumber: string) => void;
  disabled?: boolean;
  countryOptions?: Array<{ code: string; label: string }>;
  placeholder?: string;
}

const NotificationPhoneField: React.FC<NotificationPhoneFieldProps> = ({
  countryCode,
  localNumber,
  onCountryCodeChange,
  onLocalNumberChange,
  disabled = false,
  countryOptions = PHONE_COUNTRY_OPTIONS,
  placeholder,
}) => {
  const { t } = useTranslation();

  const rule = getPhoneNumberRule(countryCode);
  const lengthMessage = getPhoneLengthMessage(countryCode);
  const lengthHint = t(lengthMessage.key, {
    defaultValue: lengthMessage.defaultValue,
    ...lengthMessage.values,
  });
  const hasLengthError =
    localNumber.length > 0 &&
    !isValidNotificationLocalNumber(localNumber, countryCode);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <Select
          value={countryCode}
          onValueChange={code =>
            onCountryCodeChange(code, clampLocalNumber(localNumber, code))
          }
          disabled={disabled}
        >
          <SelectTrigger className="w-[130px] bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {countryOptions.map(option => (
              <SelectItem key={option.code} value={option.code}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={rule.maxDigits}
          aria-invalid={hasLengthError}
          aria-describedby={hasLengthError ? LENGTH_HINT_ID : undefined}
          value={localNumber}
          onChange={e =>
            onLocalNumberChange(clampLocalNumber(e.target.value, countryCode))
          }
          placeholder={
            placeholder ??
            t(
              'agent.new.notification_phone_number.placeholder.translation',
              'Optional phone number for notifications'
            )
          }
          disabled={disabled}
          className={`bg-card ${
            hasLengthError
              ? 'border-destructive focus-visible:ring-destructive'
              : ''
          }`}
        />
      </div>
      {hasLengthError && (
        <p id={LENGTH_HINT_ID} className="text-xs text-destructive">
          {lengthHint}
        </p>
      )}
    </div>
  );
};

export default NotificationPhoneField;
